import AuthManager from "Common/AuthManager";
import ServiceWrapper from "Services/ServiceWrapper";
import AppSettings from "Settings/AppSettings";

const API_BASE_URL = AppSettings.GMS_BACKEND_API_URL;


const coSoldSortFieldMap: Record<string, string> = {
    "co_sold_category": "category_name",
    "co_sold_category_type": "category_type_name",
};

export const fetchCategoriesAndCategoryTypes = async (params: {
    search?: string;
    channelKey?: string;
    sort_by?: SortOption;
    sort_order?: SortOrder;
    filters?: FilterOptions;
    data_source?: string;
    limit?: number;
    co_sort_by?: SortOption;
    co_sort_order?: SortOrder;
}) => {
    try {
        const queryParams = new URLSearchParams();
        if (params.sort_by) queryParams.append('sort_by', params.sort_by);
        if (params.sort_order) queryParams.append('sort_order', params.sort_order.toString());
        if (params.search) queryParams.append('search', params.search);
        if (params.data_source) queryParams.append('data_source', params.data_source);
        if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
        if (params.co_sort_by) {
            const coSortField = coSoldSortFieldMap[params.co_sort_by] ?? params.co_sort_by;
            queryParams.append('co_sort_by', coSortField);
        }
        if (params.co_sort_order !== undefined) queryParams.append('co_sort_order', params.co_sort_order.toString());

        if (params.filters) {
            if (params.filters.categories && params.filters.categories.length > 0) {
                const categoryIds = params.filters.categories.map(cat =>
                    typeof cat === 'string' ? cat : cat.category_id
                );
                const categoryIdsString = categoryIds.join(',');
                queryParams.append('categories', categoryIdsString);
            }

            if (params.filters.category_types && params.filters.category_types.length > 0) {
                const categoryTypeIds = params.filters.category_types.map(type =>
                    typeof type === 'string' ? type : type.category_type_id
                );
                const categoryTypeIdsString = categoryTypeIds.join(',');
                queryParams.append('category_types', categoryTypeIdsString);
            }
            if (params.filters.co_sold_categories && params.filters.co_sold_categories.length > 0) {
                const coSoldCategoryIds = params.filters.co_sold_categories.map(cat =>
                    typeof cat === 'string' ? cat : cat.category_id
                );
                const coSoldCategoryIdsString = coSoldCategoryIds.join(',');
                queryParams.append('co_sold_categories', coSoldCategoryIdsString);
            }

            if (params.filters.co_sold_category_types && params.filters.co_sold_category_types.length > 0) {
                const coSoldCategoryTypeIds = params.filters.co_sold_category_types.map(type =>
                    typeof type === 'string' ? type : type.category_type_id
                );
                const coSoldCategoryTypeIdsString = coSoldCategoryTypeIds.join(',');
                queryParams.append('co_sold_category_types', coSoldCategoryTypeIdsString);
            }
            if (params.filters.is_curated !== undefined) {
                queryParams.append('is_curated', params.filters.is_curated.toString());
            }
        }

        const url = `${API_BASE_URL}relevant_category/categories/${params.channelKey}?${queryParams}`;
        const response = await ServiceWrapper.doGet(
            url,
            null,
            null,
            AuthManager.getGmsToken());

        if (response.errorfound) {
            throw new Error('Failed to fetch categories');
        }

        return response.data;
    } catch (error) {
        console.error('Error fetching categories:', error);
        throw error;
    }
};

export const fetchCoSoldCategories = async (
    categoryId: string,
    channelKey: string,
    params: {
        search?: string;
        limit?: number;
        filters?: FilterOptions;
        is_curated?: boolean;
        sort_by?: SortOption;
        sort_order?: SortOrder;
        data_source?: string;
    }
) => {
    try {
        const queryParams = new URLSearchParams();

        if (params.limit) {
            queryParams.append('limit', params.limit.toString());
        }

        if (params.sort_by !== undefined && params.sort_order !== undefined) {
            if (!["category_name", "category_type_name"].includes(params.sort_by)) {
                const sortFieldMap: Record<string, string> = {
                    "co_sold_category": "category_name",
                    "co_sold_category_type": "category_type_name",
                };
                const sortField = sortFieldMap[params.sort_by] || params.sort_by;
                queryParams.append('sort_by', sortField);
                queryParams.append('sort_order', params.sort_order.toString());
            }
        }

        if (params.data_source) queryParams.append('data_source', params.data_source);

        if (params.filters) {

            if (params.search) queryParams.append('search', params.search);

            if (params.filters.co_sold_categories && params.filters.co_sold_categories.length > 0) {
                const coSoldCategoryIds = params.filters.co_sold_categories.map(cat =>
                    typeof cat === 'string' ? cat : cat.category_id
                );
                const coSoldCategoryIdsString = coSoldCategoryIds.join(',');
                queryParams.append('co_sold_categories', coSoldCategoryIdsString);
            }

            if (params.filters.co_sold_category_types && params.filters.co_sold_category_types.length > 0) {
                const coSoldCategoryTypeIds = params.filters.co_sold_category_types.map(type =>
                    typeof type === 'string' ? type : type.category_type_id
                );
                const coSoldCategoryTypeIdsString = coSoldCategoryTypeIds.join(',');
                queryParams.append('co_sold_category_types', coSoldCategoryTypeIdsString);
            }

            if (params.filters.is_curated !== undefined) {
                queryParams.append('is_curated', params.filters.is_curated.toString());
            }
        }

        const url = `${API_BASE_URL}relevant_category/co_sold_categories/${channelKey}/${categoryId}?${queryParams}`;
        const response = await ServiceWrapper.doGet(url, null, null, AuthManager.getGmsToken());

        if (response.errorfound) {
            throw new Error('Failed to fetch co-sold categories');
        }

        return response.data;
    } catch (error) {
        console.error('Error fetching co-sold categories:', error);
        throw error;
    }
};

export const updateReviewStatus = async (channelId: string, parentCategoryId: string, coSoldCategoryId: string, isCurated: boolean) => {
    try {
        const url = `${API_BASE_URL}relevant_category/update_curation/${channelId}`;
        const data = [
            {
                "category_id": parentCategoryId,
                "co_sold_categories": [
                    {
                        "category_id": coSoldCategoryId,
                        "review_status": "Reviewed",
                        "active": isCurated
                    }
                ]
            }
        ];

        const response = await ServiceWrapper.doPut(url,
            data,
            null,
            AuthManager.getGmsToken());

        if (response.errorfound) {
            throw new Error('Failed to update review status');
        }

        return response.data;
    } catch (error) {
        console.error('Error updating review status:', error);
        throw error;
    }
}

export const triggerFullCompute = async (channelKey: string, excludedCategories: string[] = []) => {
    try {
        const url = `${API_BASE_URL}relevant_category/compute/${channelKey}`;
        const response = await ServiceWrapper.doPost(url, { excluded_categories: excludedCategories }, null, AuthManager.getGmsToken());

        if (response.errorfound) {
            throw new Error('Failed to trigger computation');
        }

        return response.data;
    } catch (error) {
        console.error('Error triggering full compute:', error);
        throw error;
    }
};

export const triggerDeltaCompute = async (channelKey: string, computeType: 'invoice_delta' | 'attribute_delta') => {
    try {
        const url = `${API_BASE_URL}relevant_category/compute_delta/${channelKey}`;
        const response = await ServiceWrapper.doPost(url, { compute_type: computeType }, null, AuthManager.getGmsToken());

        if (response.errorfound) {
            throw new Error('Failed to trigger delta computation');
        }

        return response.data;
    } catch (error) {
        console.error('Error triggering delta compute:', error);
        throw error;
    }
};

export const getComputeStatus = async (channelKey: string) => {
    try {
        const url = `${API_BASE_URL}relevant_category/compute_status/${channelKey}`;
        const response = await ServiceWrapper.doGet(url, null, null, AuthManager.getGmsToken());

        if (response.errorfound) {
            throw new Error('Failed to fetch compute status');
        }

        return response.data;
    } catch (error) {
        console.error('Error fetching compute status:', error);
        throw error;
    }
};

export const getWeightageConfig = async (channelKey: string) => {
    try {
        const url = `${API_BASE_URL}relevant_category/weightage_config/${channelKey}`;
        const response = await ServiceWrapper.doGet(url, null, null, AuthManager.getGmsToken());

        if (response.errorfound) {
            throw new Error('Failed to fetch weightage config');
        }

        return response.data;
    } catch (error) {
        console.error('Error fetching weightage config:', error);
        throw error;
    }
};

export const updateWeightageConfig = async (channelKey: string, invoiceWeight: number, attributeWeight: number) => {
    try {
        const url = `${API_BASE_URL}relevant_category/weightage_config/${channelKey}`;
        const response = await ServiceWrapper.doPut(url, { invoice_weight: invoiceWeight, attribute_weight: attributeWeight }, null, AuthManager.getGmsToken());

        if (response.errorfound) {
            throw new Error('Failed to update weightage config');
        }

        return response.data;
    } catch (error) {
        console.error('Error updating weightage config:', error);
        throw error;
    }
};

export const createCompatibilityRule = async (params: {
    name: string;
    description: string;
    asymmetric: boolean;
    industryId: string;
    leftCategoryId: string;
    rightCategoryId: string;
}): Promise<{ done: boolean; id: string } | null> => {
    try {
        const payload = {
            name: params.name,
            rule_type: 'CBA',
            rule_status: 0,
            description: params.description,
            asymmetric: params.asymmetric,
            industry_id: params.industryId,
            left_category: params.leftCategoryId,
            right_category: params.rightCategoryId,
            commit: false,
            rule_body: JSON.stringify({
                leftCatID: params.leftCategoryId,
                rightCatID: params.rightCategoryId,
                rule: { rule: [] },
            }),
        };
        const url = `${API_BASE_URL}rule/create`;
        const response = await ServiceWrapper.doPost(url, payload, null, AuthManager.getGmsToken());
        if (response.errorfound) throw new Error('Failed to create rule');
        return response.data as { done: boolean; id: string };
    } catch (error) {
        console.error('Error creating compatibility rule:', error);
        throw error;
    }
};

export const toggleCuration = async (channelId: string, parentCategoryId: string, coSoldCategoryId: string, isCurated: boolean) => {
    try {
        const url = `${API_BASE_URL}relevant_category/update_curation/${channelId}`;
        const data = [
            {
                "co_sold_categories": [
                    {
                        "category_id": coSoldCategoryId,
                        "active": isCurated
                    }
                ],
                "category_id": parentCategoryId
            }
        ];

        const response = await ServiceWrapper.doPut(url,
            data,
            null,
            AuthManager.getGmsToken());

        if (response.errorfound) {
            throw new Error('Failed to toggle curation');
        }

        return response.data;
    } catch (error) {
        console.error('Error toggling curation:', error);
        throw error;
    }
}

export const fetchCompatibilityRules = async (channelKey: string, categoryIdA: string, categoryIdB: string) => {
    try {
        const url = `${API_BASE_URL}relevant_category/compatibility_rules/${channelKey}/${categoryIdA}/${categoryIdB}`;
        const response = await ServiceWrapper.doGet(url, null, null, AuthManager.getGmsToken());

        if (response.errorfound) {
            throw new Error('Failed to fetch compatibility rules');
        }

        return response.data;
    } catch (error) {
        console.error('Error fetching compatibility rules:', error);
        throw error;
    }
};
